import type { PdfState } from "./types";

export const cancelRenderTask = async (
  state: PdfState,
  key: "renderTask" | "previewRenderTask",
) => {
  const task = state[key];
  if (!task) return;
  try {
    task.cancel();
    await task.promise;
  } catch {
    /* cancelled render — expected */
  }
  state[key] = null;
};

export const destroyPdfDocument = async (state: PdfState) => {
  await cancelRenderTask(state, "renderTask");
  await cancelRenderTask(state, "previewRenderTask");

  if (state.page) {
    state.page.cleanup();
    state.page = null;
  }

  if (state.document) {
    try {
      state.document.destroy();
    } catch {
      /* already destroyed */
    }
    state.document = null;
  }

  if (state.loadingTask) {
    try {
      state.loadingTask.destroy();
    } catch {
      /* already destroyed */
    }
    state.loadingTask = null;
  }

  if (state.worker) {
    try {
      state.worker.destroy();
    } catch {
      /* already destroyed */
    }
    state.worker = null;
  }

  state.currentUrl = null;
};

/**
 * Synchronous cancellation for use in useEffect cleanup (which cannot be async).
 * Fires cancel/destroy without awaiting — the `cancelled` flag in the effect
 * prevents any in-flight work from taking effect after this runs.
 */
export const cancelAllInFlight = (state: PdfState) => {
  const tasks = ["renderTask", "previewRenderTask"] as const;
  for (const key of tasks) {
    if (state[key]) {
      try {
        state[key]!.cancel();
      } catch {
        /* already cancelled */
      }
      state[key] = null;
    }
  }

  if (state.loadingTask) {
    try {
      state.loadingTask.destroy();
    } catch {
      /* already destroyed */
    }
    state.loadingTask = null;
  }

  if (state.worker) {
    try {
      state.worker.destroy();
    } catch {
      /* already destroyed */
    }
    state.worker = null;
  }
};

export const isRenderCancelledError = (error: unknown): boolean =>
  error instanceof Error && error.message?.includes("Rendering cancelled");
